import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import SpinningLoader from "@card-capture/app/components/SpinningLoader";
import {
  exchangeCode,
  createCheckoutCustomerApiClient,
  type CheckoutCustomerApiClient,
} from "@checkout-customer-api/services/customerApi.service";
import { parseAuthToken } from "@checkout-customer-api/model/authToken.model";
import { isMitCofType, isPaid } from "@checkout-customer-api/model/paymentIntent.model";
import type { ExchangeOptions, OrderDetails } from "@checkout-customer-api/model/exchange.model";
import {
  deriveCheckoutPageEnvironmentFromOrigin,
  type Environment,
} from "../model/internal.config";
import ErrorMessage from "../components/ErrorMessage";

export type RedirectOutcome =
  | { status: "SUCCESS"; paymentIntentId: string }
  | { status: "ERROR"; error: string; code?: string; paymentIntentId: string }
  | { status: "CANCEL"; paymentIntentId: string };

export interface AppContextValue {
  client: CheckoutCustomerApiClient;
  paymentIntentId: string;
  participantId: string;
  authToken: string;
  options: ExchangeOptions;
  orderDetails: OrderDetails;
  callbackUrl: string;
  environment: Environment;
  authenticationId?: string;
  isMitRegistration: boolean;
  redirect: (outcome: RedirectOutcome) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export function redirectTo(callbackUrl: string, outcome: RedirectOutcome): void {
  const url = new URL(callbackUrl);
  url.searchParams.set("status", outcome.status);
  if (outcome.status === "ERROR") {
    url.searchParams.set("error", outcome.error);
    if (outcome.code) url.searchParams.set("code", outcome.code);
  }
  url.searchParams.set("paymentIntentId", outcome.paymentIntentId);
  window.location.href = url.toString();
}

function hasErrorsArray(error: unknown): error is { errors: { type: string }[] } {
  return (
    typeof error === "object" &&
    error !== null &&
    Array.isArray((error as { errors?: unknown }).errors) &&
    (error as { errors: unknown[] }).errors.length > 0
  );
}

function hasErrorType(error: unknown): error is { type: string } {
  return (
    typeof error === "object" &&
    error !== null &&
    typeof (error as { type?: unknown }).type === "string"
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function errorTypeFrom(error: unknown): string {
  if (hasErrorsArray(error)) return error.errors[0].type;
  if (hasErrorType(error)) return error.type;
  return "CLIENT_INTERNAL_ERROR";
}

type ProviderState =
  | { view: "loading" }
  | { view: "error" }
  | { view: "ready"; value: AppContextValue };

// eslint-disable-next-line react-refresh/only-export-components
export const AppContext = createContext<AppContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useAppContext(): AppContextValue {
  const value = useContext(AppContext);
  if (!value) throw new Error("useAppContext must be used within an AppProvider");
  return value;
}

export function AppContextProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [state, setState] = useState<ProviderState>({ view: "loading" });

  useEffect(() => {
    void (async () => {
      const code = new URLSearchParams(window.location.search).get("code");
      if (!code) {
        setState({ view: "error" });
        return;
      }

      const { customerApiUrl, environment } = deriveCheckoutPageEnvironmentFromOrigin();

      let response;
      try {
        response = await exchangeCode({ baseUrl: customerApiUrl, code });
      } catch {
        setState({ view: "error" });
        return;
      }

      let claims;
      try {
        claims = parseAuthToken(response.auth_token);
      } catch {
        setState({ view: "error" });
        return;
      }
      const paymentIntentId = claims.payment_intent_id;

      const client = createCheckoutCustomerApiClient({
        baseUrl: customerApiUrl,
        token: response.auth_token,
      });

      try {
        const { order_details: orderDetails } = response;
        if (!orderDetails) {
          throw new Error("order_details is missing");
        }

        const intent = await client.initPayment(paymentIntentId);

        if (isPaid(intent)) {
          redirectTo(response.callback_url, {
            status: "SUCCESS",
            paymentIntentId: intent.id,
          });
          return;
        }

        setState({
          view: "ready",
          value: {
            client,
            paymentIntentId,
            participantId: claims.participant_id,
            authToken: response.auth_token,
            options: response.options ?? {},
            orderDetails,
            callbackUrl: response.callback_url,
            environment,
            authenticationId: intent.authentication_id,
            isMitRegistration: isMitCofType(intent),
            redirect: (outcome) => redirectTo(response.callback_url, outcome),
          },
        });
      } catch (error) {
        redirectTo(response.callback_url, {
          status: "ERROR",
          error: errorTypeFrom(error),
          paymentIntentId,
        });
      }
    })();
  }, []);

  if (state.view === "error") {
    return <ErrorMessage />;
  }
  if (state.view === "loading") {
    return <SpinningLoader />;
  }
  return <AppContext.Provider value={state.value}>{children}</AppContext.Provider>;
}
